import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../icons/CloseIcon';

import style from './Modal.module.scss';

const sizesMap = {
  sm: 'modal-width-320',
  md: 'modal-width-500',
  lg: 'modal-width-700',
  default: 'modal-width-500',
};

const Modal = ({
  open,
  onClose,
  showCloseIcon = true,
  hideGutters,
  closeIconHoverText,
  closeOnBackdropClick,
  customBaseClass,
  customContainerClass,
  customInnerClass,
  children,
  size,
  withOutsideCloseIcon,
}) => {
  const className = 'c-Modal';

  const modalEl = document.getElementById('modal');

  const baseClassNames = classNames(className, style[className], {
    [style[`${className}--open`]]: open,
  });

  const containerClassNames = classNames(
    `${className}__container`,
    style[`${className}__container`],
    hideGutters && style[`${className}__container__no-gutter`],
    {
      [customContainerClass]: !!customContainerClass,
    },
    style[sizesMap?.[size]],
  );

  const innerClassNames = classNames(style[`${className}__inner`], {
    [customInnerClass]: !!customInnerClass,
  });

  const closeModalIconClassName = classNames(
    `${className}__close-modal`,
    style[`${className}__close-modal`],
    {
      [`${style[`${className}__outside-close-modal`]}`]: withOutsideCloseIcon,
    },
  );

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) onClose();
  };

  useLayoutEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;

    if (document.body && open) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (document.body) document.body.style.overflow = originalStyle;
    };
  }, [open]);

  const renderCloseIcon = useCallback(
    () =>
      showCloseIcon && (
        <div className={closeModalIconClassName} onClick={onClose}>
          {closeIconHoverText ? (
            <div className={style[`${className}__tooltip`]}>
              <span className={style[`${className}__tooltiptext`]}>
                {closeIconHoverText}
              </span>
              <CloseIcon />
            </div>
          ) : (
            <CloseIcon />
          )}
        </div>
      ),
    [closeIconHoverText, closeModalIconClassName, onClose, showCloseIcon],
  );

  if (!modalEl) return null;

  return createPortal(
    <div className={`${baseClassNames} ${customBaseClass}`} data-testid="modal">
      <div
        className={style[`${className}__backdrop`]}
        onClick={handleBackdropClick}
      />
      {withOutsideCloseIcon && renderCloseIcon()}
      <div className={containerClassNames}>
        {!withOutsideCloseIcon && renderCloseIcon()}

        <div className={innerClassNames}>{children}</div>
      </div>
    </div>,
    modalEl,
  );
};

Modal.defaultProps = {
  size: 'default',
  open: false,
  closeOnBackdropClick: true,
  hideGutters: false,
  closeIconHoverText: '',
  customBaseClass: '',
  customContainerClass: '',
  customInnerClass: '',
};

// No need to pass in onClose function if showCloseIcon is false, by default it is true
Modal.propTypes = {
  size: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  showCloseIcon: PropTypes.bool,
  hideGutters: PropTypes.bool,
  children: PropTypes.any.isRequired,
  closeIconHoverText: PropTypes.string,
  closeOnBackdropClick: PropTypes.bool,
  customBaseClass: PropTypes.string.isRequired,
  customContainerClass: PropTypes.string.isRequired,
  customInnerClass: PropTypes.string.isRequired,
  withOutsideCloseIcon: PropTypes.bool,
};

export default Modal;
